/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Platform,
  ViewStyle,
} from 'react-native';
import { pd, getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import ContentCardView from '@airasia-phoenix/react-native/widgets/ContentCardView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { useLogger } from '@airasia-rn/core-logger';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  useAppConfig,
  useLocale,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';

import { createCardProps, getLocalizedText } from './utils';
import {
  ADULTS_COUNT,
  CHILDREN,
  HOTEL_LOB,
  INFANTS,
} from '../../configs/constants';
import { useFeaturedDestinations } from '../../hooks/hotels';
import { SkeletonLoaderWrapper } from '../../components/SkeletonLoaderWrapper';
import { FeaturedDestination } from '../../models';
import { buildHotelListUrl, formatDateValue } from '../../utils';
import { RoomConfig } from '../../types';
import { useParsedSettings } from '../../hooks/useParsedSettings';
import { useHandleHotelListNavigation } from '../../hooks/useHandleHotelListNavigation';
import { useHotelSearchFormState } from '../../hooks/useHotelSearchFormState';
import { useFeaturedDestinationOrigin } from '../../hooks/useFeaturedDestinationOrigin';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';

export interface FeatureDestinationContentProps {
  data: FeaturedDestination[];
  isLoading: boolean;
  languageId: string;
  error: Error | null;
  onNavigate?: () => void;
}
interface ContentCardListItemProps {
  item: FeaturedDestination;
  languageId: string;
  onDestinationClick: (item: FeaturedDestination) => void;
}

const ContentCardListItem = React.memo(
  ({ item, languageId, onDestinationClick }: ContentCardListItemProps) => (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={() => onDestinationClick(item)}
    >
      <View style={styles.cardContainer}>
        <ContentCardView {...createCardProps(item, languageId)} />
      </View>
    </TouchableOpacity>
  ),
);
ContentCardListItem.displayName = 'ContentCardListItem';

export const FeatureDestinationContent = ({
  data,
  isLoading,
  languageId,
  error,
  onNavigate,
}: FeatureDestinationContentProps) => {
  const { t } = useTranslation('hotels');
  const { logEvent } = useLogger();
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { navigateToHotelListUrl, getHotelListRoutePath } =
    useHandleHotelListNavigation();
  const hasTrackedRef = useRef(false);
  const { EVENT_NAMES } = SearchWidgetMeta;
  const featureDestinationTitle = React.useMemo(
    () => t('searchHotelPage.featuredDestination.title'),
    [t],
  );

  const { default_checkin, default_checkout } = useParsedSettings([
    'default_checkin',
    'default_checkout',
  ]);
  const { getFormState } = useHotelSearchFormState();
  const { markFeaturedDestinationOrigin } = useFeaturedDestinationOrigin();

  // Memoize default rooms configuration to prevent recreation
  const defaultRooms: RoomConfig[] = React.useMemo(
    () => [
      {
        id: '1',
        adults: ADULTS_COUNT,
        children: CHILDREN,
        infants: INFANTS,
      },
    ],
    [],
  );

  const onDestinationClick = React.useCallback(
    (item: FeaturedDestination) => {
      // Fire featured destination clicked event
      const analyticsParams = SearchWidgetEvent.featuredDestinationClickedEvent(
        {
          destinationId: item.airPortCode,
        },
      );
      logEvent(EVENT_NAMES.FEATURED_DESTINATION_CLICKED, analyticsParams);

      // Read the shared search form state fresh at click time (not the
      // `formState` closed over at last render) so a date just confirmed in
      // the calendar is always reflected, even if this component's own
      // re-render from that update hasn't landed yet (see useHotelSearchFormState).
      const currentFormState = getFormState();
      let checkInDate = currentFormState.checkIn;
      let checkOutDate = currentFormState.checkOut;

      if (!checkInDate || !checkOutDate) {
        checkInDate = new Date();
        checkOutDate = new Date();

        if (
          typeof default_checkin === 'number' &&
          typeof default_checkout === 'number'
        ) {
          checkInDate.setDate(checkInDate.getDate() + default_checkin);
          checkOutDate.setDate(checkOutDate.getDate() + default_checkout);
        }
      }

      const listPageUrl = buildHotelListUrl({
        routePath: getHotelListRoutePath(),
        destination: {
          id: item.destinationId,
          name: item.text ? getLocalizedText(languageId, item.text) : '',
          country_code: item.airPortCode,
          type: HOTEL_LOB,
          aa_id: '',
          city_id: '',
          coordinates: {
            lat: 0,
            lon: 0,
          },
          full_name: '',
        },
        checkIn: checkInDate,
        checkOut: checkOutDate,
        rooms: defaultRooms,
        currency: currency || DEFAULT_CURRENCY,
        countryCode: appConfig.geoId || 'IN',
        languageId: appConfig.locale || DEFAULT_LANGUAGE,
      });

      // Native initialParams (pinned from a hotel-list deeplink on the
      // Hotels.tsx embed path) are merged into every results entry. Append the
      // canonical keys so the tapped destination wins; otherwise a stale
      // deep-link location_id/checkin/checkout survives the merge and
      // parseHotelSearchParams (which prefers location_id over destination)
      // re-fetches the deeplink location instead of this tile's destination.
      const canonicalSearchParams = new URLSearchParams({
        location_id: item.destinationId,
        checkin: formatDateValue(checkInDate),
        checkout: formatDateValue(checkOutDate),
        country_code: appConfig.geoId || 'IN',
      });
      const finalUrl = `${listPageUrl}&${canonicalSearchParams.toString()}`;

      // A featured tap is not a search. Record it so the search page keeps the
      // destination field on the user's last real search when the user comes
      // back, instead of adopting this tile's destination.
      markFeaturedDestinationOrigin(item.destinationId);

      onNavigate?.();
      navigateToHotelListUrl(finalUrl);
    },
    [
      logEvent,
      getFormState,
      markFeaturedDestinationOrigin,
      default_checkin,
      default_checkout,
      languageId,
      appConfig.geoId,
      appConfig.locale,
      currency,
      getHotelListRoutePath,
      navigateToHotelListUrl,
      defaultRooms,
      onNavigate,
    ],
  );

  useEffect(() => {
    if (!hasTrackedRef.current && data.length > 0) {
      const destinationCodes = data.map(
        (destination) => destination.airPortCode,
      );
      const analyticsParams = SearchWidgetEvent.featuredDestinationViewedEvent({
        destinationCodes,
      });
      logEvent(EVENT_NAMES.FEATURED_DESTINATION_VIEWED, analyticsParams);
      hasTrackedRef.current = true;
    }
  }, [data.length]); // Only depend on data length, not functions or objects

  if ((!isLoading || error) && data.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <View style={styles.dealsContainer} testID="feature-destination-loader">
        <SkeletonLoaderWrapper
          height={172}
          itemWidth={150}
          itemCount={3}
          orientation="horizontal"
          containerStyle={{ gap: pd.ref.space.spacing10.px }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.textContainer}>
        <TextView
          style={getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          )}
          text={featureDestinationTitle}
        />
      </View>
      <FlatList
        data={data}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scrollContainer}
        scrollEventThrottle={16}
        // eslint-disable-next-line react/prop-types -- TypeScript provides type safety for item.destinationId
        keyExtractor={(item) => item.destinationId}
        renderItem={({ item }) => (
          <ContentCardListItem
            item={item}
            languageId={languageId}
            onDestinationClick={onDestinationClick}
          />
        )}
        // eslint-disable-next-line react/prop-types -- TypeScript provides type safety
      />
    </View>
  );
};

interface FeatureDestinationViewProps {
  onNavigate?: () => void;
}

const FeatureDestinationView = ({
  onNavigate,
}: FeatureDestinationViewProps) => {
  const {
    data: featureDestinations = [],
    isLoading,
    error,
  } = useFeaturedDestinations();
  const { currentLanguage } = useLocale();
  const locale = currentLanguage || DEFAULT_LANGUAGE;

  return (
    <FeatureDestinationContent
      data={featureDestinations}
      isLoading={isLoading}
      languageId={locale}
      error={error}
      onNavigate={onNavigate}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingBottom: pd.ref.space.spacing30.px,
    paddingLeft: pd.ref.space.spacing40.px,
  },
  dealsContainer: {
    paddingBottom: pd.ref.space.spacing30.px,
    paddingLeft: pd.ref.space.spacing40.px,
  },
  textContainer: {
    marginBottom: pd.ref.space.spacing20.px,
  },
  cardContainer: {
    marginRight: pd.ref.space.spacing30.px,
    marginLeft: -pd.ref.space.spacing10.px,
  },
  scrollContainer: Platform.select({
    web: {
      flexGrow: 0,
      width: '100%',
      touchAction: 'pan-x',
      overflow: 'auto',
    },
    default: {
      flexGrow: 0,
    },
  }) as ViewStyle,
});

export default FeatureDestinationView;
